/* src/components/pages/ExtraerLibroVentasAnualEnr.jsx - Modificado el 21-02-2026 */
/* -------- On Demand LibroVentas ANUAL enrolado -------- */

import React, { useState, useEffect, useCallback, useMemo } from "react";

import { Box, Stack, TextField, Button, Typography, MenuItem, LinearProgress } from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

import { RiFileSearchLine } from "react-icons/ri";

import api from "../../services/api.js";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import { CustomToolbar } from "../../components/datagrid/CustomToolbar";

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";

const ExtraerLibroVentasAnualEnr = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* ===============================
     Estados principales
  =============================== */

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Enrolados asociados */
  const [enrolados, setEnrolados] = useState([]);
  const [rutSeleccionado, setRutSeleccionado] = useState("");

  /* ✅ Progreso visible */
  const [progresoTexto, setProgresoTexto] = useState("");
  const [progress, setProgress] = useState(null);

  /* Async Job */
  const [jobId, setJobId] = useState(null);

  /* Año */
  const [ano, setAno] = useState(new Date().getFullYear());

  /* Pagination */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ===============================
     Validación Rut Cliente
  =============================== */
  const rutValido = useMemo(() => {
    if (!user?.usuario) return false;
    return validarRut(user.usuario);
  }, [user?.usuario]);

  /* ===============================
     Validación Rut Enrolado
  =============================== */

  const rutEnrValido = useMemo(() => {
    if (!rutSeleccionado) return false;
    return validarRut(rutSeleccionado);
  }, [rutSeleccionado]);

  /* ===============================
     Cargar enrolados asociados
  =============================== */

  useEffect(() => {
    if (!user?.usuario) return;

    (async () => {
      try {
        const RutCli = normalizeRut(user.usuario);

        const { data } = await api.post("/enrolados/enroladosasociados", {
          RutCli,
          RutEnr: "",
          Tipousr: 1,
        });

        if (data?.success && data?.rows?.length) {
          setEnrolados(data.rows);
        } else {
          setEnrolados([]);
          showFeedback({
            message: "No se encontraron enrolados asociados",
            severity: "warning",
          });
        }
      } catch {
        showFeedback({
          message: "No fue posible cargar enrolados",
          severity: "error",
        });
      }
    })();
  }, [user?.usuario, showFeedback]);

  /* ===============================
     Bitácora server-side
  =============================== */
  const fetchBitacora = useCallback(async () => {
    if (!rutValido) return;

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const { data } = await api.post("/libroventas/getbitacora", {
        RutCli,
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      showFeedback({
        message: "❌ No fue posible obtener la bitácora",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [rutValido, user?.usuario, page, pageSize, showFeedback]);

  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);


  /* ===============================
    Polling Async Job (ANUAL)
  =============================== */
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/extraerlibroventas/anual/status/${jobId}`
        );

        /* ✅ Progreso live robusto */
        if (data?.progress && typeof data.progress === "object") {
          setProgress(data.progress);

          if (data.progress.message) {
            setProgresoTexto(data.progress.message);
          }
        }

        /* ✅ Mensaje inicial solo si aún no hay texto */
        if (data.state === "active") {
          setProgresoTexto((prev) =>
            prev || "⏳ Iniciando extracción anual..."
          );
        }

        /* ✅ Job eliminado → tratar como terminado */
        if (data.state === "notfound") {
          clearInterval(interval);

          showFeedback({
            message: "⚠️ Job ya no existe (probablemente terminó)",
            severity: "warning",
          });

          await fetchBitacora();

          setJobId(null);
          setProgresoTexto("");
          setProgress(null);
          return;
        }

        /* ✅ Completed */
        if (data.state === "completed") {
          clearInterval(interval);

          setProgresoTexto("🎉 Extracción anual completada");

          showFeedback({
            message: "✅ Extracción anual LibroVentas finalizada",
            severity: "success",
          });

          await fetchBitacora();

          setTimeout(() => {
            setProgresoTexto("");
            setProgress(null);
          }, 5000);

          setJobId(null);
          return;
        }

        /* ❌ Failed */
        if (data.state === "failed") {
          clearInterval(interval);

          setProgresoTexto("");
          setProgress(null);
          setJobId(null);

          showFeedback({
            message: "❌ Falló extracción anual LibroVentas",
            severity: "error",
          });
          return;
        }
      } catch {
        clearInterval(interval);

        setProgresoTexto("");
        setProgress(null);
        setJobId(null);

        showFeedback({
          message: "❌ No fue posible consultar estado del Job",
          severity: "error",
        });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [jobId, fetchBitacora, showFeedback]);

  /* ===============================
     Solicitar extracción anual
  =============================== */
  const solicitarExtraccionAnual = async () => {
    if (!rutValido) {
      showFeedback({
        message: "El Rut del cliente no es válido",
        severity: "error",
      });
      return;
    }

    if (!rutEnrValido) {
      showFeedback({
        message: "Debe seleccionar un enrolado válido",
        severity: "error",
      });
      return;
    }

    try {
      const RutCli = normalizeRut(user.usuario);
      const RutEnr = normalizeRut(rutSeleccionado);

      showFeedback({
        message: `⏳ Iniciando extracción anual Ventas ${ano}...`,
        severity: "info",
      });

      const { data } = await api.post("/extraerlibroventas/anual/start", {
        RutCli,
        RutEnr,
        TipCli: 2,  /* ✅ Enrolado */
        Tipoing: 1, /* acceso con credenciales */
        Anoaconsultar: ano,
      });

      if (data?.success) {
        setJobId(data.jobId);

        showFeedback({
          message: "📌 Proceso anual ejecutándose en segundo plano",
          severity: "info",
        });
      } else {
        showFeedback({
          message: data?.message || "No fue posible iniciar extracción anual",
          severity: "error",
        });
      }
    } catch {
      showFeedback({
        message: "❌ Error al iniciar extracción anual",
        severity: "error",
      });
    }
  };

  /* ===============================
     DataGrid Columns
  =============================== */
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "fecha",
        headerName: "Fecha",
        width: 200,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (!params.value) return "—";
          return new Date(params.value).toLocaleString("es-CL");
        },
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutcli",
        headerName: "Rut Cliente",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutenr",
        headerName: "Rut Enrolado",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "nombre",
        headerName: "Razón Social",
        flex: 1,
        minWidth: 280,
        align: "left",
        headerAlign: "center",
      },
      {
        field: "detalle",
        headerName: "Detalle",
        width: 400,
        align: "center",
        headerAlign: "center",
      },
    ],
    []
  );

  /* ===============================
     Render
  =============================== */
  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      {/* Barra controles */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
       >
        {/* Rut Cliente */}
        <TextField
          label="Rut Cliente"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Nombre Cliente */}
        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Selector enrolado */}
        <TextField
          select
          label="Enrolado"
          value={rutSeleccionado}
          size="small"
          onChange={(e) => setRutSeleccionado(e.target.value)}
          sx={{
            minWidth: 260,
            flexGrow: 1,
          }}
        >
          {enrolados.length > 0 ? (
            enrolados.map((item) => (
              <MenuItem key={item.rut_enr} value={item.rut_enr}>
                {item.nombre_enr || item.rut_enr}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay enrolados</MenuItem>
          )}
        </TextField>

        {/* Año */}
        <TextField
          select
          label="Año"
          value={ano}
          onChange={(e) => setAno(e.target.value)}
          size="small"
          sx={{ width: 120 }}
        >
          {Array.from({ length: 7 }, (_, i) => (
            <MenuItem key={i} value={new Date().getFullYear() - i}>
              {new Date().getFullYear() - i}
            </MenuItem>
          ))}
        </TextField>

        {/* Botón */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine />}
          onClick={solicitarExtraccionAnual}
          disabled={!!jobId}   // ✅ bloqueo
          sx={{
            minWidth: 130,
            height: 40,
            backgroundColor: (t) => t.custom.ui.primaryAction,
            "&:hover": {
              backgroundColor: (t) => t.custom.ui.primaryActionHover,
            },
          }}
        >
          Solicitar Año Completo
        </Button>
      </Stack>

      {/* Progreso visible */}
      {jobId && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {progresoTexto || "⏳ Ejecutando extracción anual..."}
          </Typography>

          {progress?.total > 0 && (
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      )}

      {/* Tarea en ejecución */}
      {jobId && (
        <Typography variant="caption" color="primary">
          📌 Job en ejecución: {jobId}
        </Typography>
      )}

      {/* Bitácora */}
      <Box sx={{ height: "calc(100vh - 230px)" }}>
        <DataGrid
          rows={bitacora}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell:focus": { outline: "none" },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} registros encontrados
      </Typography>
    </Box>
  );
};

export default ExtraerLibroVentasAnualEnr;
